/**
 * Timezone-safe date helpers.
 *
 * Why this file exists
 * --------------------
 * Every date in this app is a *calendar* date (a date of birth, a date of hire, an
 * enrollment date). It has no time and no timezone - "15 May 1990" means the same thing
 * in Toronto, Los Angeles and Mumbai.
 *
 * JavaScript disagrees. `new Date('1990-05-15')` is parsed as **UTC midnight**, but
 * `getFullYear()/getMonth()/getDate()` read back in the **browser's local timezone**. In
 * any timezone west of Greenwich (all of Canada and the USA) that combination reports the
 * *previous* day, so a date of birth silently walked back one day per conversion - and
 * two days when a value was converted twice. Ahead of Greenwich (India, UTC+5:30) the
 * error cancels out, which is why this was never seen while all customers were in India.
 *
 * The rule enforced here: a calendar date is only ever built from, and read as, **local**
 * year/month/day components. `Date.parse` is never used on a date-only string, and
 * `toISOString()` is never used to render one.
 *
 * Formats
 * -------
 *   API_DATE_FORMAT     'YYYY-MM-DD'  - what the backend receives. Unchanged.
 *   DISPLAY_DATE_FORMAT 'MMM-DD-YYYY' - what the user sees, e.g. May-15-1990.
 *   TYPING_DATE_FORMAT  'MM-DD-YYYY'  - what the user types, e.g. 05-15-1990.
 */

export const API_DATE_FORMAT = 'YYYY-MM-DD';
export const DISPLAY_DATE_FORMAT = 'MMM-DD-YYYY';
export const TYPING_DATE_FORMAT = 'MM-DD-YYYY';

export const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_INDEX_BY_NAME: { [name: string]: number } = (() => {
  const map: { [name: string]: number } = {};
  const full = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
  ];
  MONTHS_SHORT.forEach((m, i) => (map[m.toLowerCase()] = i));
  full.forEach((m, i) => (map[m] = i));
  return map;
})();

// YYYY-MM-DD, optionally followed by a time part we deliberately ignore.
const RE_YMD = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/;
// MM-DD-YYYY / MM/DD/YYYY
const RE_MDY = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/;
// MMM-DD-YYYY / May 15 1990
const RE_NDY = /^([A-Za-z]{3,9})[-/.\s](\d{1,2})[-/.\s,]+(\d{4})$/;
// DD-MMM-YYYY / 15 May 1990
const RE_DNY = /^(\d{1,2})[-/.\s]([A-Za-z]{3,9})[-/.\s,]+(\d{4})$/;
// Anything carrying an explicit time or zone is a real timestamp, not a calendar date.
const RE_HAS_TIME = /[T\s]\d{1,2}:\d{2}|Z$|GMT|[+-]\d{2}:?\d{2}$/;

/**
 * Smallest and largest year treated as a real calendar date.
 *
 * ngx-bootstrap's parser is lenient about short years: typing `12-12-024` gives year 24
 * and `12-12-202` gives year 202, with no error. Those are typos, not dates, and letting
 * them through puts values like '0024-12-12' in the payload. Anything outside this range
 * is reported as unparseable so it is dropped rather than sent on.
 */
const MIN_YEAR = 1000;
const MAX_YEAR = 9999;

/** Builds a local-midnight Date, rejecting impossible days such as 02-31. */
function makeLocalDate(year: number, monthIndex: number, day: number): Date | null {
  if (
    !isFinite(year) || !isFinite(monthIndex) || !isFinite(day) ||
    year < MIN_YEAR || year > MAX_YEAR ||
    monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31
  ) {
    return null;
  }
  // setFullYear rather than the `new Date(year, ...)` constructor: the constructor remaps
  // years 0-99 to 1900-1999, which would silently turn a year-24 typo into 1924.
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setFullYear(year, monthIndex, day);
  // Guards against roll-over (e.g. Feb 31 becoming Mar 3).
  if (d.getFullYear() !== year || d.getMonth() !== monthIndex || d.getDate() !== day) {
    return null;
  }
  return d;
}

/**
 * Parses any date shape used in this app into a local-midnight Date.
 * Returns null for empty/unparseable input - never an Invalid Date.
 *
 * Accepts: Date, timestamp, 'YYYY-MM-DD' (with or without a time part), 'MM-DD-YYYY',
 * 'MM/DD/YYYY', 'MMM-DD-YYYY', 'DD-MMM-YYYY', and full timestamps.
 */
export function toLocalDate(value: any): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return makeLocalDate(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime())
      ? null
      : makeLocalDate(d.getFullYear(), d.getMonth(), d.getDate());
  }

  if (typeof value !== 'string') return null;

  const raw = value.trim();
  if (!raw || raw.toLowerCase() === 'invalid date' || raw.toLowerCase() === 'null') {
    return null;
  }

  let m = RE_YMD.exec(raw);
  if (m) return makeLocalDate(+m[1], +m[2] - 1, +m[3]);

  m = RE_MDY.exec(raw);
  if (m) return makeLocalDate(+m[3], +m[1] - 1, +m[2]);

  m = RE_NDY.exec(raw);
  if (m) {
    const monthIndex = MONTH_INDEX_BY_NAME[m[1].toLowerCase()];
    if (monthIndex === undefined) return null;
    return makeLocalDate(+m[3], monthIndex, +m[2]);
  }

  m = RE_DNY.exec(raw);
  if (m) {
    const monthIndex = MONTH_INDEX_BY_NAME[m[2].toLowerCase()];
    if (monthIndex === undefined) return null;
    return makeLocalDate(+m[3], monthIndex, +m[1]);
  }

  // Last resort: only trust the engine's parser when the string carries a real time,
  // because that is the one case where the UTC/local distinction is genuinely meaningful.
  if (RE_HAS_TIME.test(raw)) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      return makeLocalDate(d.getFullYear(), d.getMonth(), d.getDate());
    }
  }

  return null;
}

/** True when the value parses to a real calendar date. */
export function isValidDateValue(value: any): boolean {
  return toLocalDate(value) !== null;
}

/**
 * 'YYYY-MM-DD' for the API payload. Idempotent: running it over its own output (which
 * happens in several submit paths) returns the same string instead of drifting a day.
 * Returns '' for empty input so payload fields stay blank rather than 'NaN-NaN-NaN'.
 */
export function toApiDate(value: any): string {
  const d = toLocalDate(value);
  if (!d) return '';
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** 'MMM-DD-YYYY' for anything the user reads, e.g. May-15-1990. */
export function toDisplayDate(value: any): string {
  const d = toLocalDate(value);
  if (!d) return '';
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${MONTHS_SHORT[d.getMonth()]}-${day}-${d.getFullYear()}`;
}

/** 'MM-DD-YYYY' - the digits-only form the user types into a datepicker. */
export function toTypingDate(value: any): string {
  const d = toLocalDate(value);
  if (!d) return '';
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${month}-${day}-${d.getFullYear()}`;
}

/**
 * Age in completed years, counting the month and day rather than only subtracting the
 * years. Returns null when the date of birth cannot be parsed, so callers can tell
 * "unknown" apart from "age 0" instead of silently treating NaN as a number.
 */
export function calculateAgeFrom(dob: any, asOf: Date = new Date()): number | null {
  const birth = toLocalDate(dob);
  if (!birth) return null;

  let age = asOf.getFullYear() - birth.getFullYear();
  const monthDiff = asOf.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/** Whole days between two calendar dates, timezone- and DST-safe. */
export function daysBetween(from: any, to: any): number | null {
  const a = toLocalDate(from);
  const b = toLocalDate(to);
  if (!a || !b) return null;
  // Compare as UTC so a DST boundary in between cannot add or drop an hour.
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((utcA - utcB) / 86400000);
}
